import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Menu,
  Tooltip,
  Typography,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { MouseEvent, useEffect, useState } from "react";
import NotificationInfo from "../molecules/NotificationInfo";
import { NotificationService } from "../../services/api";
import { NotificationInstance } from "../../services/swagger";
import useRefresh from "../../hooks/useRefresh";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGlobalContext } from "../../stores/Global";
import useBreakpoint from "../../hooks/useBreakpoint";

export default function NotificationButton() {
  const { t } = useTranslation("NotificationButton");
  const navigate = useNavigate();
  const increment = 5;
  const [limit, setLimit] = useState(5);
  const [count, setCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationInstances, setNotificationInstances] = useState<
    NotificationInstance[]
  >([]);
  const { context, setContext } = useGlobalContext();
  const breakpoint = useBreakpoint();

  const loadNotifications = async () => {
    const res = await NotificationService.getMyNotifications({ limit });
    if (res?.data?.data) {
      setNotificationInstances(res.data.data.docs);
      setCount(res.data.data.count);
    }
  };

  useRefresh(loadNotifications);

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClearNotifications = async () => {
    const res = await NotificationService.updateNotificationsClear();
    if (res?.data?.success) loadNotifications();
  };

  useEffect(() => {
    if (!navigator.serviceWorker) return;
    navigator.serviceWorker.onmessage = (e) => {
      const message = e.data;

      if (message?.type === "navigate" && message?.url) {
        window.location.href = message.url;
      }

      if (!message) return;
      loadNotifications();
    };
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [limit]);

  return (
    <>
      <Tooltip title={t("Notifications")}>
        <ButtonBase sx={{ position: "relative" }} onClick={handleClick}>
          {notificationInstances.length > 0 && (
            <CircleIcon
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                transform: "translateX(30%) translateY(-30%)",
                zIndex: 2,
                width: 8,
                height: 8,
                color: (theme) => theme.palette.error.light,
              }}
            />
          )}
          <Avatar
            variant="rounded"
            sx={{
              width: 25,
              height: 25,
              border: (theme) => `2px solid ${theme.palette.primary.main}`,
              backgroundColor: "transparent", // make it transparent
            }}
          >
            <NotificationsNoneIcon
              fontSize="small"
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 2,
            sx: {
              width: 300,
              bgcolor: "background.default",
              overflow: "auto",
              mt: 1.5,
            },
          },
        }}
        sx={{ zIndex: context.sidebar && !breakpoint.md ? 2000 : undefined }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        {notificationInstances.length > 0 ? (
          <>
            {notificationInstances.map((instance, i) => {
              return (
                <NotificationInfo
                  key={i}
                  notification={instance.notification}
                  date={instance.date}
                  onClick={() => {
                    handleClose();
                    if (instance.notification.urlPath)
                      navigate(instance.notification.urlPath);
                  }}
                />
              );
            })}
            {limit < count && (
              <Button fullWidth onClick={() => setLimit((l) => l + increment)}>
                {t("LoadMore")}
              </Button>
            )}
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ textAlign: "right", pr: 1 }}>
              <Button onClick={handleClearNotifications}>
                {t("ClearNotifications")}
              </Button>
            </Box>
          </>
        ) : (
          <Typography mt={1} mb={1} align="center" color="text.secondary">
            {t("NoNotificationsFound")}
          </Typography>
        )}
      </Menu>
    </>
  );
}
